import { useQuery, useQueryClient } from "@tanstack/react-query";
import { callWithAuth, getDictionaryStats } from "@/api/Api";
import { DICTIONARY_STATS_QUERY_KEY } from "@/query_keys";
import { GetDictionaryStatsResponse } from "@shared_types";

/**
 * Custom hook for managing dictionary statistics
 * @returns Object with dictionary stats and methods to update them
 */
export const useDictionaryStats = () => {
  const queryClient = useQueryClient();

  // Query for fetching dictionary stats
  const query = useQuery({
    queryKey: [DICTIONARY_STATS_QUERY_KEY],
    queryFn: () => callWithAuth(getDictionaryStats),
  });

  /**
   * Optimistically update known word count
   * @param amount Number of known words to add (can be negative for subtraction)
   */
  const addKnownWords = (amount: number) => {
    if (amount === 0) return;

    queryClient.setQueryData(
      [DICTIONARY_STATS_QUERY_KEY],
      (oldData: GetDictionaryStatsResponse | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          knownWordCount: (oldData.knownWordCount || 0) + amount,
        };
      }
    );
  };

  /**
   * Optimistically update learning word count
   * @param amount Number of learning words to add (can be negative for subtraction)
   */
  const addLearningWords = (amount: number) => {
    if (amount === 0) return;

    queryClient.setQueryData(
      [DICTIONARY_STATS_QUERY_KEY],
      (oldData: GetDictionaryStatsResponse | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          learningWordCount: (oldData.learningWordCount || 0) + amount,
        };
      }
    );
  };

  /**
   * Optimistically update known phrase count
   * @param amount Number of known phrases to add (can be negative for subtraction)
   */
  const addKnownPhrases = (amount: number) => {
    if (amount === 0) return;

    queryClient.setQueryData(
      [DICTIONARY_STATS_QUERY_KEY],
      (oldData: GetDictionaryStatsResponse | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          knownPhraseCount: (oldData.knownPhraseCount || 0) + amount,
        };
      }
    );
  };

  /**
   * Optimistically update learning phrase count
   * @param amount Number of learning phrases to add (can be negative for subtraction)
   */
  const addLearningPhrases = (amount: number) => {
    if (amount === 0) return;

    queryClient.setQueryData(
      [DICTIONARY_STATS_QUERY_KEY],
      (oldData: GetDictionaryStatsResponse | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          learningPhraseCount: (oldData.learningPhraseCount || 0) + amount,
        };
      }
    );
  };

  const learningWordCount = query.data?.learningWordCount || 0;
  const knownWordCount = query.data?.knownWordCount || 0;
  const learningPhraseCount = query.data?.learningPhraseCount || 0;
  const knownPhraseCount = query.data?.knownPhraseCount || 0;
  const totalWordCount = knownWordCount + learningWordCount;
  const totalPhraseCount = knownPhraseCount + learningPhraseCount;

  // Mirrors iOS useDictionaryStats: once the learner has accrued >= 100 total
  // words, the header/feed badge flips to showing the *known* count ("words
  // known"); below that it shows the total ("words"). Single source of truth so
  // feed/header/etc. don't re-derive the 100-word threshold.
  const showKnownBadge = totalWordCount >= 100;
  const badgeCount = showKnownBadge ? knownWordCount : totalWordCount;

  return {
    knownWordCount: knownWordCount,
    learningWordCount: learningWordCount,
    knownPhraseCount: knownPhraseCount,
    learningPhraseCount: learningPhraseCount,
    totalWordCount: totalWordCount,
    totalPhraseCount: totalPhraseCount,
    showKnownBadge: showKnownBadge,
    badgeCount: badgeCount,
    isPending: query.isPending,
    error: query.error,
    addKnownWords,
    addLearningWords,
    addKnownPhrases,
    addLearningPhrases,
  };
};
